import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Eye, EyeOff, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/moneleg`;
const selectClass = "h-9 rounded-md border border-slate-300 bg-white px-2 text-sm";
const errorMsg = (e) => e.response?.data?.detail || "Error";

export default function MonelegAdmin() {
  const [lotes, setLotes] = useState([]);
  const [max, setMax] = useState(15);
  const [lote, setLote] = useState(null);
  const [disponibles, setDisponibles] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [provincia, setProvincia] = useState("");
  const [search, setSearch] = useState("");
  const [nuevo, setNuevo] = useState({ nombre: "", region: "" });
  const [cuenta, setCuenta] = useState({ configurada: false });
  const [password, setPassword] = useState("");
  const [descargas, setDescargas] = useState([]);

  const cargarLotes = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/lotes`);
    setLotes(data.lotes);
    setMax(data.max);
  }, []);

  const cargarLote = useCallback(async (id) => {
    const { data } = await axios.get(`${API}/admin/lotes/${id}`);
    setLote(data);
  }, []);

  const cargarDisponibles = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/disponibles`, { params: { provincia, search } });
    setDisponibles(data.dossiers);
    setProvincias(data.provincias);
  }, [provincia, search]);

  const cargarDescargas = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/descargas`);
    setDescargas(data.descargas);
  }, []);

  useEffect(() => {
    cargarLotes();
    cargarDescargas();
    axios.get(`${API}/admin/cuenta`).then(({ data }) => setCuenta(data));
  }, [cargarLotes, cargarDescargas]);

  useEffect(() => { cargarDisponibles(); }, [cargarDisponibles]);

  const accion = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(errorMsg(e));
    }
  };

  const crearLote = () => accion(async () => {
    const { data } = await axios.post(`${API}/admin/lotes`, nuevo);
    setNuevo({ nombre: "", region: "" });
    await cargarLotes();
    await cargarLote(data.id);
  }, "Lote creado");

  const guardarLote = (cambios, ok) => accion(async () => {
    await axios.put(`${API}/admin/lotes/${lote.id}`, cambios);
    await Promise.all([cargarLotes(), cargarLote(lote.id)]);
  }, ok);

  const borrarLote = () => {
    if (!window.confirm(`¿Eliminar el ${lote.nombre}? Los dossiers no se tocan.`)) return;
    accion(async () => {
      await axios.delete(`${API}/admin/lotes/${lote.id}`);
      setLote(null);
      await Promise.all([cargarLotes(), cargarDisponibles()]);
    }, "Lote eliminado");
  };

  const anadir = (dossierId) => accion(async () => {
    const url = `${API}/admin/lotes/${lote.id}/dossiers`;
    try {
      await axios.post(url, { dossier_id: dossierId });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status !== 409 || !d?.confirmar) throw e;
      if (!window.confirm(`⚠ ${d.mensaje}`)) return;
      await axios.post(url, { dossier_id: dossierId, confirmar: true });
    }
    await Promise.all([cargarLote(lote.id), cargarLotes(), cargarDisponibles()]);
  });

  const quitar = (dossierId) => accion(async () => {
    await axios.delete(`${API}/admin/lotes/${lote.id}/dossiers/${dossierId}`);
    await Promise.all([cargarLote(lote.id), cargarLotes(), cargarDisponibles()]);
  });

  const guardarPassword = () => accion(async () => {
    await axios.put(`${API}/admin/cuenta`, { password });
    setCuenta({ ...cuenta, configurada: true, password });
    setPassword("");
  }, "Contraseña guardada");

  const borrarAcceso = () => {
    if (!window.confirm("¿Eliminar el acceso de Moneleg? Dejará de poder entrar. Los lotes no se tocan.")) return;
    accion(async () => {
      await axios.delete(`${API}/admin/cuenta`);
      setCuenta({ ...cuenta, configurada: false });
    }, "Acceso eliminado");
  };

  const publicado = lote?.estado === "publicado";

  return (
    <div className="space-y-4 max-w-5xl">
      <h1 className="text-2xl font-semibold">Dossier Moneleg</h1>

      <Card>
        <CardHeader><CardTitle className="text-lg">Lotes</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {lotes.map((l) => (
            <button
              key={l.id}
              onClick={() => cargarLote(l.id)}
              className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left text-sm ${lote?.id === l.id ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}
            >
              <span className="flex-1">{l.nombre}{l.region ? ` · ${l.region}` : ""}<span className="block text-xs text-slate-500">{l.total} de {max} dossiers</span></span>
              <span className={`text-xs px-2 py-0.5 rounded ${l.estado === "publicado" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{l.estado === "publicado" ? "Publicado" : "Borrador"}</span>
            </button>
          ))}
          <div className="flex gap-2 pt-2">
            <Input placeholder="Nombre del lote" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
            <Input placeholder="Región" value={nuevo.region} onChange={(e) => setNuevo({ ...nuevo, region: e.target.value })} />
            <Button onClick={crearLote} disabled={!nuevo.nombre.trim()}><Plus className="w-4 h-4 mr-1" />Nuevo lote</Button>
          </div>
        </CardContent>
      </Card>

      {lote && (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <CardTitle className="text-lg flex-1">{lote.nombre} · configuración</CardTitle>
            <Button variant="outline" onClick={() => guardarLote({ estado: publicado ? "borrador" : "publicado" }, publicado ? "Lote retirado" : "Lote publicado")}>
              {publicado ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}{publicado ? "Retirar" : "Publicar"}
            </Button>
            <Button variant="outline" onClick={borrarLote}><Trash2 className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input defaultValue={lote.nombre} key={`n${lote.id}`} onBlur={(e) => e.target.value.trim() !== lote.nombre && guardarLote({ nombre: e.target.value }, "Guardado")} />
              <Input defaultValue={lote.region} key={`r${lote.id}`} placeholder="Región" onBlur={(e) => e.target.value.trim() !== lote.region && guardarLote({ region: e.target.value }, "Guardado")} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">En el lote ({lote.total} de {max})</p>
                {lote.dossiers.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 text-sm">
                    <span className="flex-1">{d.reference}<span className="block text-xs text-slate-500">{d.cliente}</span></span>
                    <button onClick={() => quitar(d.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" aria-label="Quitar"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Disponibles</p>
                <div className="flex gap-2">
                  <select className={selectClass} value={provincia} onChange={(e) => setProvincia(e.target.value)}>
                    <option value="">Todas las provincias</option>
                    {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <Input placeholder="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                {!disponibles.length && <p className="text-sm text-slate-500">Ningún dossier en estado Enviado Moneleg.</p>}
                {disponibles.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 text-sm">
                    <span className="flex-1">{d.reference}<span className="block text-xs text-slate-500">{d.cliente}</span></span>
                    <button onClick={() => anadir(d.id)} className="p-1 text-sky-600 hover:bg-sky-50 rounded" aria-label="Añadir"><Plus className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Acceso de Moneleg</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${cuenta.configurada ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
            <span className="flex-1 font-medium">
              {cuenta.configurada ? "Acceso activo" : "Sin acceso creado"}
              <span className="block text-xs font-normal text-slate-600">Usuario: <code>moneleg</code>{cuenta.password && <> · Contraseña: <code>{cuenta.password}</code></>} · <a href="/moneleg" target="_blank" rel="noreferrer" className="text-sky-600 underline">{window.location.origin}/moneleg</a></span>
            </span>
            {cuenta.configurada && <Button variant="outline" onClick={borrarAcceso}><Trash2 className="w-4 h-4 mr-1" />Eliminar acceso</Button>}
          </div>
          <div className="flex gap-2 max-w-md">
            <Input type="password" placeholder="Contraseña (mínimo 8 caracteres)" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button onClick={guardarPassword} disabled={password.length < 8}>{cuenta.configurada ? "Cambiar contraseña" : "Crear acceso"}</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Descargas de Moneleg</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          {!descargas.length && <p className="text-slate-500">Sin descargas todavía.</p>}
          {descargas.map((d, i) => (
            <p key={i}>
              <span className="text-slate-500">{new Date(d.at).toLocaleString("es-ES")}</span>{" · "}
              {d.tipo === "zip_lote" ? `ZIP del lote ${d.lote}` : d.tipo === "zip_dossier" ? `ZIP ${d.dossier}` : `${d.documento} · ${d.dossier}`}
            </p>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
